import { Component } from '@builder.io/sdk';
import { MarginField, AnchorField } from '../common/CommonFields';
import { linkRegexConfig } from '../utils/commonFields';

const customerService: { tabName: string; path: string; components: Record<string, Component> } = {
  tabName: 'Customer Service',
  path: 'customerService',
  components: {
    CustomerServiceHero: {
      name: 'Hero',
      inputs: [
        {
          name: 'background',
          type: 'object',
          subFields: [
            {
              name: 'desktopImage',
              type: 'file',
              allowedFileTypes: ['jpg', 'jpeg', 'svg', 'png'],
              required: true,
            },
            {
              name: 'mobileImage',
              type: 'file',
              allowedFileTypes: ['jpg', 'jpeg', 'svg', 'png'],
              required: true,
            },
          ],
        },
        {
          name: 'imgAlt',
          type: 'text',
          required: true,
        },
        { name: 'title', type: 'text', required: true },
        {
          name: 'titleTag',
          type: 'string',
          defaultValue: 'h2',
          helperText: 'Defaults to h2 tag',
          enum: ['h1', 'h2'],
        },
        { name: 'description', type: 'richText' },
        {
          name: 'isWhiteBackgroundVisible',
          friendlyName: 'Show white background',
          type: 'boolean',
          defaultValue: true,
        },
        {
          name: 'textBoxPosition',
          friendlyName: 'Desktop text box position',
          type: 'text',
          defaultValue: 'right',
          enum: ['left', 'right'],
        },
        {
          name: 'textColor',
          friendlyName: 'Desktop text color',
          type: 'text',
          defaultValue: 'black',
          enum: ['black', 'white'],
        },
        MarginField,
        AnchorField,
      ],
    },
    CustomerServiceTiles: {
      name: 'CategoryTiles',
      inputs: [
        {
          name: 'sectionHeader',
          type: 'object',
          subFields: [
            {
              name: 'optionalLargeHeader',
              type: 'object',
              subFields: [
                {
                  name: 'content',
                  type: 'text',
                },
                {
                  name: 'titleHTMLTag',
                  type: 'string',
                  defaultValue: 'h2',
                  helperText: 'Defaults to h2 tag',
                  enum: ['h1', 'h2'],
                },
              ],
            },
            { name: 'optionalDescription', type: 'richText' },
          ],
        },
        {
          name: 'headerPosition',
          type: 'text',
          defaultValue: 'center',
          enum: ['left', 'center'],
        },
        {
          name: 'isAddOutline',
          friendlyName: 'OutlineToggle',
          type: 'boolean',
          defaultValue: false,
        },
        {
          name: 'isTwoIconOneLine',
          type: 'boolean',
          defaultValue: false,
          helperText: 'Display two icons in one row',
        },
        {
          name: 'linkGroups',
          friendlyName: 'Items',
          type: 'list',
          helperText: 'There should be 2-4 items',
          subFields: [
            {
              name: 'icon',
              type: 'object',
              subFields: [
                {
                  name: 'iconSrc',
                  type: 'file',
                  allowedFileTypes: ['jpg', 'jpeg', 'svg', 'png'],
                },
                {
                  name: 'Icon Alt Text',
                  type: 'string',
                },
              ],
            },
            {
              name: 'label',
              type: 'object',
              subFields: [
                {
                  name: 'labelTag',
                  friendlyName: 'HTML Tag',
                  type: 'string',
                  enum: ['h2', 'h3'],
                  defaultValue: 'h3',
                },
                {
                  name: 'labelText',
                  friendlyName: 'Label',
                  type: 'text',
                },
              ],
            },
            {
              name: 'linkDescription',
              friendlyName: 'Description',
              type: 'richText',
            },
            {
              name: 'isLinkedGroup',
              friendlyName: 'Pop-up',
              helperText:
                'Switch this toggle on if clicking the icon, title, and description should open a pop-up (i.e. Live Person, T&C Pop-up, etc.).',
              type: 'boolean',
              defaultValue: false,
            },
            {
              name: 'link',
              friendlyName: 'Link',
              type: 'url',
              showIf: `!options.get('isLinkedGroup')`,
              regex: { ...linkRegexConfig },
            },
            {
              name: 'onClick',
              friendlyName: 'Pop-up',
              type: 'string',
              required: false,
              showIf: `options.get('isLinkedGroup')`,
              enum: ['openLivePerson', 'openPromoModal', 'openEmailUsModal'],
            },
            {
              name: 'gaEvents',
              type: 'list',
              subFields: [
                {
                  name: 'dataAttributes',
                  type: 'list',
                  subFields: [
                    {
                      name: 'dataAttributeName',
                      type: 'text',
                    },
                    {
                      name: 'dataAttributeValue',
                      type: 'text',
                    },
                  ],
                },
              ],
            },
          ],
        },
        MarginField,
        AnchorField,
      ],
    },
    CustomerServiceNavigation: {
      name: 'CustomerServiceNavigation',
      inputs: [
        {
          name: 'title',
          type: 'text',
          required: true,
        },
        {
          name: 'headers',
          type: 'list',
          subFields: [
            {
              name: 'label',
              type: 'text',
              required: true,
            },
            {
              name: 'link',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
            {
              name: 'subheaders',
              type: 'list',
              subFields: [
                {
                  name: 'label',
                  type: 'text',
                  required: true,
                },
                {
                  name: 'link',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                },
              ],
            },
          ],
        },
        AnchorField,
      ],
    },
    CustomerServiceCopyBlockHalf: {
      name: 'CustomerServiceCopyBlockHalf',
      inputs: [
        {
          name: 'imgSrc',
          type: 'file',
          allowedFileTypes: ['jpg', 'jpeg', 'svg', 'png'],
          required: true,
        },
        {
          name: 'imgAlt',
          type: 'text',
          required: true,
        },
        {
          name: 'imgLink',
          type: 'url',
          regex: { ...linkRegexConfig },
        },
        {
          name: 'header',
          type: 'text',
          required: true,
        },
        {
          name: 'description',
          type: 'richText',
        },
        {
          name: 'isMediaLeft',
          friendlyName: 'Media Left Toggle',
          type: 'boolean',
        },
        {
          name: 'actions',
          type: 'list',
          subFields: [
            {
              name: 'label',
              type: 'text',
            },
            {
              name: 'variant',
              friendlyName: 'Desktop variant',
              type: 'text',
              enum: ['primary', 'secondary'],
              defaultValue: 'primary',
            },
            {
              name: 'color',
              friendlyName: 'Desktop color',
              type: 'text',
              enum: ['light', 'dark'],
              defaultValue: 'dark',
            },
            {
              name: 'mobileVariant',
              type: 'text',
              enum: ['primary', 'secondary'],
              defaultValue: 'primary',
            },
            {
              name: 'mobileColor',
              type: 'text',
              enum: ['light', 'dark'],
              defaultValue: 'dark',
            },
            {
              name: 'isDropdownButton',
              type: 'boolean',
              defaultValue: false,
              friendlyName: 'Is this a dropdown button?',
              helperText:
                'Please keep the Href field empty if it is a dropdown button. A dropdown button is a button that on hover displays multiple actions to be chosen.',
            },
            {
              name: 'href',
              type: 'url',
              regex: { ...linkRegexConfig },
              showIf: `!options.get('isDropdownButton')`,
            },
            {
              name: 'actions',
              type: 'list',
              subFields: [
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'href',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                },
              ],
              showIf: `options.get('isDropdownButton')`,
            },
          ],
        },
        MarginField,
        AnchorField,
      ],
    },
    CustomerServiceCopyBlockFull: {
      name: 'CustomerServiceCopyBlockFull',
      inputs: [
        {
          name: 'imgSrc',
          type: 'file',
          allowedFileTypes: ['jpg', 'jpeg', 'svg', 'png'],
        },
        {
          name: 'mobileImgSrc',
          type: 'file',
          allowedFileTypes: ['jpg', 'jpeg', 'svg', 'png'],
        },
        {
          name: 'imgAlt',
          type: 'text',
        },
        {
          name: 'imgLink',
          type: 'url',
          regex: { ...linkRegexConfig },
        },
        {
          name: 'header',
          type: 'text',
        },
        {
          name: 'headerTag',
          type: 'string',
          defaultValue: 'h2',
          helperText: 'Defaults to h2 tag',
          enum: ['h1', 'h2'],
        },
        {
          name: 'description',
          type: 'richText',
        },
        {
          name: 'textPosition',
          type: 'text',
          defaultValue: 'center',
          enum: ['left', 'center'],
        },
        {
          name: 'actions',
          type: 'list',
          helperText: 'Add at most one CTA',
          subFields: [
            {
              name: 'label',
              type: 'text',
            },
            {
              name: 'variant',
              friendlyName: 'Desktop variant',
              type: 'text',
              enum: ['primary', 'secondary'],
              defaultValue: 'primary',
            },
            {
              name: 'color',
              friendlyName: 'Desktop color',
              type: 'text',
              enum: ['light', 'dark'],
              defaultValue: 'dark',
            },
            {
              name: 'mobileVariant',
              type: 'text',
              enum: ['primary', 'secondary'],
              defaultValue: 'primary',
            },
            {
              name: 'mobileColor',
              type: 'text',
              enum: ['light', 'dark'],
              defaultValue: 'dark',
            },
            {
              name: 'isDropdownButton',
              type: 'boolean',
              defaultValue: false,
              friendlyName: 'Is this a dropdown button?',
              helperText:
                'Please keep the Href field empty if it is a dropdown button. A dropdown button is a button that on hover displays multiple actions to be chosen.',
            },
            {
              name: 'href',
              type: 'url',
              regex: { ...linkRegexConfig },
              showIf: `!options.get('isDropdownButton')`,
            },
            {
              name: 'actions',
              type: 'list',
              subFields: [
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'href',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                },
              ],
              showIf: `options.get('isDropdownButton')`,
            },
          ],
        },
        MarginField,
        AnchorField,
      ],
    },
    FAQs: {
      name: 'FAQs',
      inputs: [
        {
          name: 'mainTitle',
          type: 'string',
        },
        {
          defaultValue: 'h2',
          helperText: 'Defaults to h2 tag',
          name: 'mainTitleTag',
          type: 'string',
          enum: ['h1', 'h2', 'h3'],
        },
        {
          name: 'FAQList',
          type: 'list',
          subFields: [
            {
              name: 'header',
              type: 'string',
            },
            {
              defaultValue: 'h2',
              helperText: 'Defaults to h2 tag',
              name: 'headerTag',
              type: 'string',
              enum: ['h1', 'h2', 'h3'],
            },
            {
              name: 'items',
              type: 'list',
              subFields: [
                {
                  name: 'title',
                  type: 'string',
                },
                {
                  name: 'titleTag',
                  type: 'string',
                  enum: ['h1', 'h2', 'h3'],
                },
                {
                  name: 'titleSize',
                  type: 'string',
                  enum: ['h2', 'h3', 'h4'],
                },
                {
                  name: 'answer',
                  type: 'richText',
                },
              ],
            },
          ],
        },
        MarginField,
        AnchorField,
      ],
    },
    Table: {
      name: 'Table',
      inputs: [
        {
          name: 'columnWidth',
          type: 'string',
        },
        {
          name: 'mobileColumnWidth',
          type: 'string',
        },
        {
          name: 'headings',
          type: 'list',
          subFields: [{ name: 'heading', type: 'string' }],
        },
        {
          name: 'rows',
          type: 'list',
          subFields: [
            {
              name: 'data',
              type: 'list',
              subFields: [
                {
                  name: 'text',
                  type: 'richText',
                },

                {
                  name: 'isLongestText',
                  helperText:
                    'only set one(the longest text) to true, in order to set Rows length dictated by longest cell',
                  type: 'boolean',
                },
              ],
            },
          ],
        },
        {
          name: 'fontWeight',
          type: 'string',
          defaultValue: '350',
          enum: ['350', '500', '800'],
        },
        {
          name: 'headerVerticalAlign',
          type: 'string',
          defaultValue: 'center',
          enum: ['center', 'top'],
        },
        {
          name: 'headerAlign',
          type: 'string',
          defaultValue: 'left',
          enum: ['center', 'left'],
        },
        {
          name: 'rowsVerticalAlign',
          type: 'string',
          defaultValue: 'center',
          enum: ['center', 'top'],
        },
        {
          name: 'rowsAlign',
          type: 'string',
          defaultValue: 'left',
          enum: ['center', 'left'],
        },
        {
          name: 'backgroundColor',
          type: 'string',
          defaultValue: '#FFFFFF',
          enum: ['#FFFFFF', '#F9F9F9', '#D9D9D9'],
        },
        AnchorField,
        MarginField,
      ],
    },
  },
};

export default customerService;
